import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Button from '../../components/button/Button';
import ExtractStatement from '../../components/extractStatement/ExtractStatement';
import SwipeToRefresh from '../../components/SwipeToRefresh/SwipeToRefresh';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastModalContext';
import { ReactComponent as LoaderSecondary } from '../../assets/SpinnerBlack.svg';
import { WalletOrder } from '../../constants/Payment';
import { getExtractHistory } from '../../services/extractService';
import {
  formatCurrency,
  formatDateWalletHistoryCharger,
  formatDateWalletHistoryFounds,
} from '../../utils/format';

import { useHeaderContext } from '../../context/HeaderContext';
import { useBrandContext } from '../../context/BrandContext';
import './Extract.css';

const isTransfer = (orderItemDesc: string) =>
  orderItemDesc === WalletOrder.SPOTT_TRANSFER_IN ||
  orderItemDesc === WalletOrder.SPOTT_TRANSFER_OUT;

const Extract: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const navigate = useNavigate();
  const [ExtractHistory, setExtractHistory] = useState([]);
  const [filter, setFilter] = useState('30');
  const filterOptions = ['30', '60', '90'];
  const authContext = useAuth();
  const brandContext = useBrandContext();
  const { setHeaderConfig } = useHeaderContext();
  const { showToast } = useToast();

  useEffect(() => {
    if (authContext.isLogged() === undefined) return;
    else if (authContext.isLogged()) {
      if (filter) getExtractData();
    } else navigate(`/${brandContext.brandName}`);
  }, [
    authContext,
    authContext.isLogged,
    brandContext.brandName,
    filter,
    navigate,
  ]);

  const getExtractData = async () => {
    try {
      setIsLoading(true);
      const { data: extractHistoryData } = await getExtractHistory(filter);
      setIsLoading(false);
      setIsError(false);
      const extractData = extractHistoryData
        ?.map((walletHistory: any) => ({
          statementId: walletHistory.uwhId,
          statementValue: formatCurrency(walletHistory.uwhAmmount),
          statementType: walletHistory.uwhUwttId === 1 ? 'debit' : 'credit',
          statementTitle:
            walletHistory.orderItemDesc === WalletOrder.SPOTT_CREDIT
              ? 'Adicionou'
              : walletHistory.orderItemDesc === 'SPOTT_EV_CHARGE_KWH'
              ? 'Recarga'
              : walletHistory.orderItemDesc === WalletOrder.SPOTT_TRANSFER_IN
              ? 'Transferência recebida'
              : walletHistory.orderItemDesc === WalletOrder.SPOTT_TRANSFER_OUT
              ? 'Transferência enviada'
              : null,
          // Transferência: a API já manda "Transferência de/para Fulano" em description.
          statementTransfer: isTransfer(walletHistory.orderItemDesc)
            ? walletHistory.orderItemDesc === WalletOrder.SPOTT_TRANSFER_IN
              ? 'in'
              : 'out'
            : null,
          statementSubtitle: isTransfer(walletHistory.orderItemDesc)
            ? walletHistory.description
            : null,
          statementDate:
            walletHistory.startTime &&
            walletHistory.stopTime &&
            walletHistory.orderItemDesc !== WalletOrder.SPOTT_CREDIT
              ? formatDateWalletHistoryCharger(
                  walletHistory.startTime,
                  walletHistory.stopTime
                )
              : formatDateWalletHistoryFounds(walletHistory.uwhTimestamp),
          statementTimestamp: walletHistory.uwhTimestamp,
          statementTransactionId: walletHistory.transactionId,
          statementUwhOrderId: walletHistory.uwhOrderId,
          statementOrderState: walletHistory.orderState,
        }))
        .sort((a: any, b: any) => a.statementTimestamp < b.statementTimestamp);
      setExtractHistory(extractData);
    } catch (er) {
      setIsLoading(false);
      setIsError(true);
      showToast({
        title: 'Falha',
        messageModal:
          'Falha ao carregar transações. Por favor, tente novamente',
        textEnterButton: 'Tentar novamente',
        allowButtonClosed: true,
        handleEnterButton: () => getExtractData(),
        closeButtonText: 'Fechar',
      });
      console.log(er);
    }
  };

  useEffect(() => {
    setHeaderConfig({
      headerText: 'Extrato',
      backNavigation: () => navigate(`/${brandContext.brandName}`),
    });
    return () => setHeaderConfig({});
  }, [brandContext.brandName, navigate, setHeaderConfig]);

  return (
    <SwipeToRefresh onSwipeDown={getExtractData}>
      <div className='recharge-extract-filters-container'>
        {filterOptions.map((value, index) => (
          <div key={index} className='recharge-extract-button-container'>
            <Button
              style={{
                backgroundColor: `${filter === value ? '#787E7D' : '#fff'}`,
                padding: '10px 28px',
                color: `${filter === value ? '#fff' : '#787E7D'}`,
                border: `${filter === value ? 'none' : '1px solid #787E7D'}`,
                fontSize: '14px',
                fontWeight: '600',
                height: '35px',
              }}
              type='button'
              children={`${value} dias`}
              onClick={() => setFilter(value)}
            />
          </div>
        ))}
      </div>
      <div className='extract-container'>
        <b className='extract-title'>Transações</b>
        <div className='extract-divider'></div>
        {ExtractHistory.length > 0 && !isLoading ? (
          ExtractHistory?.map((value: any, index) => (
            <div key={index}>
              <ExtractStatement
                onClick={() => {
                  // Transferência não é recarga nem adição: tem o próprio detalhe. Antes caía
                  // em recharge/undefined (saída) ou no recibo de adição (entrada).
                  value.statementTransfer
                    ? navigate(`transfer/${value.statementUwhOrderId}`, {
                        state: value,
                      })
                    : value.statementType === 'debit'
                    ? navigate(`recharge/${value.statementTransactionId}`)
                    : navigate(`credit/${value.statementUwhOrderId}`);
                }}
                extract={value}
              />
              <div className='extract-divider'></div>
            </div>
          ))
        ) : !isLoading && !isError ? (
          <span>Não há transações</span>
        ) : (
          !isError && <LoaderSecondary />
        )}
      </div>
    </SwipeToRefresh>
  );
};

export default Extract;
